import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  jsonb,
  index,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants
// ---------------------------------------------------------------------------

export const COMMUNICATION_CHANNELS = ["phone", "email", "video", "in_person", "chat", "sms"] as const;
export type CommunicationChannel = (typeof COMMUNICATION_CHANNELS)[number];

export const COMMUNICATION_DIRECTIONS = ["inbound", "outbound"] as const;
export type CommunicationDirection = (typeof COMMUNICATION_DIRECTIONS)[number];

export const EVIDENCE_TYPES = ["recording", "document", "screenshot", "transcript"] as const;
export type EvidenceType = (typeof EVIDENCE_TYPES)[number];

// ---------------------------------------------------------------------------
// JSONB type interfaces
// ---------------------------------------------------------------------------

export interface CommunicationContent {
  blocks?: Array<Record<string, unknown>>;
  rawText?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// 1. Client Communication Log — APPEND-ONLY proof-of-service records
// ---------------------------------------------------------------------------
// IMMUTABILITY: This table MUST be append-only at the service layer.
// No UPDATE, no DELETE. Each entry is a legal proof-of-service record.
// Disputes are resolved via annotations, not mutations.
//
// PII: fullContent may contain verbatim client conversations (LEVEL 2+ PII).
// Encrypted at rest via KEK/DEK. summary may contain manually-entered PII.
// piiClassification JSONB tracks data sensitivity per record.

export const clientCommunicationLog = pgTable("client_communication_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  personId: uuid("person_id"), // Contact involved (nullable for internal notes)
  dealId: uuid("deal_id"), // Deal context (nullable)
  channel: text("channel").notNull(), // CommunicationChannel
  direction: text("direction").notNull(), // CommunicationDirection
  subject: text("subject"),
  summary: text("summary"), // May contain PII — see piiClassification
  // CQ4: fullContent encrypted at rest via KEK/DEK (verbatim conversations = LEVEL 2+ PII)
  fullContent: jsonb("full_content").$type<CommunicationContent>(),
  encryptionKeyId: text("encryption_key_id"), // KEK version for fullContent encryption
  piiClassification: jsonb("pii_classification"), // Data sensitivity classification per record
  durationSeconds: integer("duration_seconds"), // For calls/meetings
  staffUserId: uuid("staff_user_id"), // Who logged this
  externalRef: text("external_ref"), // External system reference (call SID, email message-id)
  // CQ4: GDPR retention period
  retentionExpiresAt: timestamp("retention_expires_at", tz),
  occurredAt: timestamp("occurred_at", tz).notNull(), // When the communication happened
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  // No updatedAt — append-only
}, (table) => [
  index("idx_comm_log_tenant").on(table.tenantId),
  index("idx_comm_log_person").on(table.tenantId, table.personId),
  index("idx_comm_log_deal").on(table.tenantId, table.dealId),
  index("idx_comm_log_channel").on(table.tenantId, table.channel),
  index("idx_comm_log_occurred").on(table.tenantId, table.occurredAt),
]);

// ---------------------------------------------------------------------------
// 2. Communication Evidence — Attachments/artifacts (append-only)
// ---------------------------------------------------------------------------
// LEGAL: Audio/video recordings may be subject to wiretapping laws
// (EU: two-party consent in many member states, US: varies by state).
// consentRecordId MUST be set before storage. Service layer enforces.
//
// R2 objects MUST be encrypted server-side. encryptedAtRest field is an
// explicit assertion verified by the service layer before accepting the record.

export const communicationEvidence = pgTable("communication_evidence", {
  id: uuid("id").primaryKey().defaultRandom(),
  communicationLogId: uuid("communication_log_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  evidenceType: text("evidence_type").notNull(), // EvidenceType
  r2Key: text("r2_key").notNull(), // R2 storage reference
  mimeType: text("mime_type").notNull(), // e.g., "audio/mp3", "application/pdf"
  fileSizeBytes: integer("file_size_bytes"),
  fileName: text("file_name"),
  // CQ7: Encryption + consent for legally sensitive recordings
  encryptionKeyId: text("encryption_key_id"), // KEK version for R2 object encryption
  encryptedAtRest: boolean("encrypted_at_rest").notNull().default(true),
  consentRecordId: uuid("consent_record_id"), // Proof recording consent was obtained
  // GDPR retention
  retentionExpiresAt: timestamp("retention_expires_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  // No updatedAt — append-only (once stored, evidence is immutable)
}, (table) => [
  index("idx_comm_evidence_tenant").on(table.tenantId),
  index("idx_comm_evidence_log").on(table.tenantId, table.communicationLogId),
]);
